import { useEffect, useMemo, useRef, useState } from 'react'
import { Card, Checkbox, Field, Row, Tag, useToast } from '../ui'
import { LEAVE_MS } from '../ui/Row'
import { useListItems, useLists } from '../lib/queries'
import { useAddItem, useCompleteTask, useSetItemDone, tickTask } from '../lib/mutations'
import { attentionItems } from '../lib/attention'
import { LINGER_MS, primaryTaskList, shortDay } from '../lib/taskRules'
import { addTask } from '../lib/listAdd'
import { berlinDay } from '../lib/today'
import { go } from '../lib/router'
import type { ListItem } from '../lib/types'
import './TasksCard.css'

/** How many due rows the card draws before "+N more". */
const CAP = 5

/**
 * The Tasks card — tasks plan T3 (D10): on the phone's Today and the desk's
 * Year. Shown only when the first task list (`primaryTaskList`, the same
 * choice the deep link makes) has an open row; absent otherwise, never
 * empty. Overdue and due-today rows (`attentionItems`, so the badge, the
 * Lists line and this card cannot disagree about who needs you), a tick
 * with the same linger as the list, `N open` to the list, and an add field
 * that parses like the list's own.
 *
 * It reads its own queries rather than taking props, so a screen's first
 * render is untouched: while either query is pending it draws nothing (the
 * cold-load trap, CLAUDE.md), and a screen that never had lists in its tree
 * does not have to learn them.
 */
export function TasksCard({ desk = false }: { desk?: boolean }) {
  const listsQ = useLists()
  const itemsQ = useListItems()
  const toast = useToast()
  const addM = useAddItem()
  const doneM = useSetItemDone()
  const completeM = useCompleteTask()
  const [draft, setDraft] = useState('')
  // D1: ids ticked within LINGER_MS — done in the data, still drawn here,
  // struck through; then `leaving`, then gone. Timers cleared on unmount.
  const [lingering, setLingering] = useState<ReadonlySet<string>>(() => new Set())
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(() => new Set())
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>[]>())
  useEffect(() => () => { for (const ts of timers.current.values()) ts.forEach(clearTimeout) }, [])

  const lists = listsQ.data
  const items = itemsQ.data
  const list = useMemo(() => (lists ? primaryTaskList(lists) : null), [lists])
  const today = berlinDay(new Date())

  const listItems = useMemo(() => (items ?? []).filter(i => i.list_id === list?.id), [items, list?.id])
  const open = useMemo(() => listItems.filter(i => !i.done_at), [listItems])
  // A lingering row is drawn as open so `attentionItems` keeps listing it.
  const due = useMemo(() => {
    const asOpen = listItems.map(i => (lingering.has(i.id) ? { ...i, done_at: null } : i))
    const ids = attentionItems([], [], asOpen, new Date()).filter(a => a.kind === 'task')
    const byId = new Map(listItems.map(i => [i.id, i]))
    return ids.map(a => ({ item: byId.get(a.id) as ListItem, why: a.why }))
  }, [listItems, lingering])

  if (!list || !items || !open.length) return null

  const drop = (id: string, set: 'lingering' | 'leaving') => {
    const fn = set === 'lingering' ? setLingering : setLeaving
    fn(prev => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n })
  }
  const cancelLinger = (id: string) => {
    timers.current.get(id)?.forEach(clearTimeout)
    timers.current.delete(id)
    drop(id, 'lingering'); drop(id, 'leaving')
  }

  const tick = (item: ListItem) => {
    if (lingering.has(item.id)) { cancelLinger(item.id); doneM.mutate({ id: item.id, done: false }); return }
    const { nextDueOn } = tickTask({ done: doneM, complete: completeM }, item, today)
    setLingering(prev => new Set(prev).add(item.id))
    const t1 = setTimeout(() => setLeaving(prev => new Set(prev).add(item.id)), LINGER_MS)
    const t2 = setTimeout(() => { drop(item.id, 'lingering'); drop(item.id, 'leaving'); timers.current.delete(item.id) }, LINGER_MS + LEAVE_MS)
    timers.current.set(item.id, [t1, t2])
    toast({
      text: `Done "${item.body}"${nextDueOn ? ` · next ${shortDay(nextDueOn)}` : ''}`,
      action: {
        label: 'Undo',
        onClick: () => {
          cancelLinger(item.id)
          doneM.mutate({ id: item.id, done: false })
          if (nextDueOn) toast({ text: 'Unticked · the next one stays' })
        },
      },
    })
  }

  const target = list
  async function add() {
    const text = draft
    if (!text.trim()) return
    const out = await addTask({ addM }, target, listItems, text)
    if (!out.ok) { toast({ text: 'Couldn\'t add it' }); return }
    if (out.empty) return
    setDraft('')
    toast({ text: `Added to ${target.name}` })
  }

  const shown = due.slice(0, CAP)
  const more = due.length - shown.length

  return (
    <Card className={`tasks-card${desk ? ' tasks-card--desk' : ''}`} aria-label="Tasks">
      <div className="tasks-card__head">
        <h2 className="tasks-card__title">{list.name}</h2>
        <button type="button" className="tasks-card__open" onClick={() => go('lists', list.id)}>
          {open.length} open
        </button>
      </div>
      {shown.length > 0 && (
        <div className="tasks-card__rows">
          {shown.map(({ item, why }) => (
            <Row
              key={item.id}
              className={`u-row--list tasks-card__row${item.done_at ? ' is-done' : ''}`}
              leaving={leaving.has(item.id)}
              leading={
                <Checkbox checked={!!item.done_at} onChange={() => tick(item)}
                  aria-label={item.done_at ? `Mark "${item.body}" not done` : `Mark "${item.body}" done`} />
              }
              title={<span className="tasks-card__body">{item.body}</span>}
              meta={null}
              trailing={<Tag tone={why === 'overdue' ? 'bad' : 'warn'}>{why === 'overdue' ? 'Overdue' : 'Today'}</Tag>}
            />
          ))}
        </div>
      )}
      {more > 0 && (
        <button type="button" className="tasks-card__more" onClick={() => go('lists', list.id)}>+{more} more</button>
      )}
      <form className="tasks-card__add" onSubmit={e => { e.preventDefault(); void add() }}>
        <Field.Input
          value={draft} autoComplete="off" aria-label={`Add a task to ${list.name}`}
          placeholder="Add a task — “Renew passport by Friday”"
          onChange={e => setDraft(e.target.value)}
        />
      </form>
    </Card>
  )
}
